import React, { useEffect, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import EditDeleteMenuButton from '../button/EditDeleteMenuButton';
import AdminDeleteButton from '../button/AdminDeleteButton';
import CancelConfirmDialog from '../dialog/CancelConfirmDialog';
import UserWithProfile from '../common/UserWithProfile';
import FavoriteButton from '../button/FavoriteButton';
import CommentReplyButton from '../button/CommentReplyButton';
import CommentReplyForm from '../common/CommentReplyForm';
import theme from '../../theme';
import CommentEditForm from '../common/CommentEditForm';
import CommentContent from '../common/CommentContent';
import useCommentActions from '../../hooks/useCommentActions';

const COMMENT_MAX_LENGTH = 5000;

const CommentReplyItem = ({
  comment,
  onEdit,
  onDelete,
  onReply,
  parentId,
  commentScrollId = null,
}) => {
  const commentRef = useRef(null);

  const {
    isEditing,
    editContent,
    setEditContent,
    isReplying,
    replyContent,
    setReplyContent,
    isLiked,
    likeCount,
    isCancelEditDialogOpen,
    isCancelReplyDialogOpen,
    isCommentAuthor,
    isAdmin,
    navigateWithScrollMemory,
    handleEditClick,
    handleEditSubmit,
    handleEditCancel,
    handleEditCancelConfirm,
    handleEditCancelCancel,
    handleReplyIconClick,
    handleReplySubmit,
    handleCancelReplyClick,
    handleCancelReplyCancel,
    handleCancelReplyConfirm,
    handleLikeToggle,
  } = useCommentActions({ comment, onEdit, onReply, replyTargetId: parentId });

  // commentScrollId가 현재 댓글인지 확인하고 스크롤
  useEffect(() => {
    if (commentScrollId === comment.id && commentRef.current) {
      // 부모 댓글의 펼치기 애니메이션을 고려하여 더 긴 지연 시간 설정
      setTimeout(() => {
        commentRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }, 300);
    }
  }, [commentScrollId, comment.id]);

  return (
    <Box ref={commentRef} sx={{ px: { xs: 2, md: 2, lg: 3 }, py: { xs: 1.5, md: 1.5, lg: 2.5 } }}>
      <Box>
        {/* 1. 프로필 정보 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, md: 2.5 },
          }}
        >
          <UserWithProfile
            username={comment.user.nickname}
            profileImageUrl={comment.user.profileImageUrl}
            userNickname={comment.user.nickname}
            grade={comment.user.grade}
            onClick={() =>
              navigateWithScrollMemory(`/users/${encodeURIComponent(comment.user.nickname)}`)
            }
            spacing={1}
          />
          {/* 어드민 삭제 버튼 */}
          {isAdmin && !isCommentAuthor && (
            <AdminDeleteButton variant="small" onDelete={() => onDelete(comment.id)} />
          )}
          {/* 작성자 메뉴 버튼 */}
          {isCommentAuthor && !comment.isDeleted && !isEditing && (
            <EditDeleteMenuButton
              variant="small"
              onEdit={handleEditClick}
              onDelete={() => onDelete(comment.id)}
            />
          )}
        </Box>

        {/* 2. 댓글 내용 */}
        {isEditing ? (
          <CommentEditForm
            editContent={editContent}
            setEditContent={setEditContent}
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
            maxLength={COMMENT_MAX_LENGTH}
          />
        ) : (
          <CommentContent
            content={comment.content}
            isDeleted={comment.isDeleted}
            mentionedUser={comment.mentionedUser}
          />
        )}
        {!isEditing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <Typography fontSize={14} color={theme.palette.black[1]} sx={{ whiteSpace: 'nowrap' }}>
              {comment.updatedAt}
            </Typography>
            {!comment.isDeleted && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: '8px',
                    alignSelf: 'center',
                    backgroundColor: theme.palette.grey.line,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <FavoriteButton
                    isLiked={isLiked}
                    onToggle={handleLikeToggle}
                    likeCount={likeCount}
                  />
                  <CommentReplyButton count={0} onClick={handleReplyIconClick} />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* 대댓글 작성 폼 */}
      <CommentReplyForm
        isOpen={isReplying}
        replyContent={replyContent}
        setReplyContent={setReplyContent}
        onSubmit={handleReplySubmit}
        onCancel={handleCancelReplyClick}
        maxLength={COMMENT_MAX_LENGTH}
        mentionedNickname={comment.user.nickname}
        mt={{ xs: 2, md: 2.5 }}
        mb={0}
      />

      {/* 취소 확인 다이얼로그 */}
      <CancelConfirmDialog
        open={isCancelReplyDialogOpen}
        onClose={handleCancelReplyCancel}
        onConfirm={handleCancelReplyConfirm}
      />
      <CancelConfirmDialog
        open={isCancelEditDialogOpen}
        onClose={handleEditCancelCancel}
        onConfirm={handleEditCancelConfirm}
      />
    </Box>
  );
};

export default CommentReplyItem;
